import { FormProps, SelectProps } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';

import { FAIL_REPORT_URLS } from '@/constants/urls';
import useFormDraftInit from '@/hooks/useFormDraftInit';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { FailReport } from '@/model/FailReport/FailReport';
import { RESOURCES } from '@/Resources';

const getRelationId = (value?: number | { id: number }): number | undefined => {
  if (value == undefined) {
    return undefined;
  }

  return typeof value === 'object' ? value.id : value;
};

const useFailReportForm = (formProps: FormProps<FailReport>, edit?: boolean) => {
  const initialValues = useMemo<Partial<FailReport>>(
    () =>
      edit
        ? {
            ...formProps.initialValues,
            category: getRelationId(formProps.initialValues?.category),
            subcategory: getRelationId(formProps.initialValues?.subcategory),
          }
        : { report_date: dayjs().format('L') },
    [formProps.initialValues, edit],
  );
  const { initialValuesSetted, formInitialValues, clearDraft, hasDraftChanges } = useFormDraftInit({
    form: formProps.form,
    draftEnabled: !edit || Boolean((initialValues as { id?: string | number })?.id),
    initialValues: initialValues,
  });
  const { ticketsSelectProps, categorySelectProps } = useOutletContext<{
    ticketsSelectProps: SelectProps;
    categorySelectProps: SelectProps;
  }>();
  // El selector de subcategorías depende de la categoría seleccionada, por lo que se le pasan los valores de filtro correspondientes
  const categoryId = formProps?.form?.getFieldValue('category');
  const { selectProps: subcategorySelectProps } = useSelectScrollSearch({
    resource: FAIL_REPORT_URLS.FAIL_SUB_CATEGORIES,
    optionLabel: item => item.title,
    optionValue: item => item.id,
    filters: categoryId ? [{ field: 'category', operator: 'eq', value: categoryId }] : [],
    queryOptions: {
      enabled: Boolean(categoryId), // Solo habilita la consulta si hay una categoría seleccionada
    },
  });
  // Datos iniciales
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: formInitialValues?.ticket,
    labelKey: (item: any) =>
      `${item.id} - ${item.title ? item.title : item.ticket_title_str ? item.ticket_title_str : '-'}`,
    valueKey: 'id',
  });
  const categorySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: categorySelectProps,
    resource: FAIL_REPORT_URLS.FAIL_CATEGORIES,
    initialValue: formInitialValues?.category,
    labelKey: 'title',
    valueKey: 'id',
  });
  const subcategorySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: subcategorySelectProps,
    resource: FAIL_REPORT_URLS.FAIL_SUB_CATEGORIES,
    initialValue: formInitialValues?.subcategory,
    labelKey: 'title',
    valueKey: 'id',
  });

  return {
    ticketsSelectProps: ticketsSelectPropsWithInitials,
    categorySelectProps: categorySelectPropsWithInitials,
    subcategorySelectProps: subcategorySelectPropsWithInitials,
    initialValuesSetted,
    formInitialValues,
    clearDraft,
    hasDraftChanges,
  };
};

export default useFailReportForm;
